import React from 'react';
import TableToolbar from '../../components/table/TableToolbar';
import Table from '../../components/table/Table';
import TableHead, { Order } from '../../components/table/TableHead';
import { descendingComparator, formatDateTime, formatDateTimeUTC, getFilters } from '../../utils';
import { logFieldHeaders, logTableFields } from './logsFieldsConfig';
import LogsDeviceRow from './logsDeviceRow';
import { Log, LogsContext, LogsState } from './logsContext';
import { Button, Checkbox, FormControl, InputLabel, ListItemText, Menu, MenuItem, OutlinedInput, Select, SelectChangeEvent, Snackbar, Stack } from '@mui/material';
import UserFetch from '../../userFetch';
import { useContextSelector } from 'use-context-selector';


function LogsTable({ isSuperAdmin }: { isSuperAdmin?: boolean }) {
  const logsDispatch = useContextSelector(LogsContext, (c) => c.dispatch);
  const logsContext = useContextSelector(LogsContext, (c) => c.state) as LogsState;
  const logs = logsContext.logs;
  const logTypes = logsContext.types;
  const logsLength = Object.keys(logs).length;

  const [typesFilter, setTypesFilter] = React.useState<string[]>([]);

  const [showUTC, setShowUTC] = React.useState<boolean>(() => {
    const saved = localStorage.getItem('logTableShowUTC');
    return saved === 'true';
  });

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  const [order, setOrder] = React.useState<Order>(() => {
    const savedOrder = localStorage.getItem('logTableOrder');
    return (savedOrder as Order) || 'desc';
  });

  const [orderBy, setOrderBy] = React.useState<keyof Log>(() => {
    const savedOrderBy = localStorage.getItem('logTableOrderBy');
    return (savedOrderBy as keyof Log) || 'timestamp';
  });

  const [filter, setFilter] = React.useState<string | undefined>(() => {
    const savedFilter = localStorage.getItem('logTableFilter');
    return savedFilter || undefined;
  });

  const [page, setPage] = React.useState(0);

  const [rowsPerPage, setRowsPerPage] = React.useState(() => {
    const saved = localStorage.getItem('logTableRowsPerPage');
    return saved ? Number(saved) : 100;
  });

  const filteredRows = React.useMemo(
    () => filterLogs(Object.values(logs), filter).filter(log => {
      if (typesFilter.length === 0) return true;
      return typesFilter.includes(log.type);
    }),
    [logs, filter, typesFilter]
  );

  // Sorted once per filter/sort change — paging only slices. Copy: sort() mutates.
  const sortedRows = React.useMemo(
    () => [...filteredRows].sort(getComparator(order, orderBy)),
    [order, orderBy, filteredRows],
  );

  const visibleRows = React.useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedRows],
  );

  // The active filter (text search + type multi-select) is the single source of
  // truth for both viewing and deleting. No filter → delete targets everything.
  const hasActiveFilter = Boolean(filter) || typesFilter.length > 0;

  const handleRequestSort = React.useCallback((_event: React.MouseEvent<unknown>, property: keyof Log, ctrlKey = false) => {
    const isAsc = orderBy === property && order === 'asc';

    const defaultDescendingFields = [] as string[];
    const isSameColumn = orderBy === property;

    let newOrder: Order;

    if (ctrlKey) {
      if (isSameColumn) {
        newOrder = isAsc ? 'asc' : 'desc';
      } else {
        newOrder = defaultDescendingFields.includes(property as string) ? 'asc' : 'desc';
      }
    } else {
      if (isSameColumn) {
        newOrder = isAsc ? 'desc' : 'asc';
      } else {
        newOrder = defaultDescendingFields.includes(property as string) ? 'desc' : 'asc';
      }
    }

    setOrder(newOrder);
    setOrderBy(property);

    localStorage.setItem('logTableOrder', newOrder);
    localStorage.setItem('logTableOrderBy', property as string);
  }, [order, orderBy]);

  const handleChangePage = React.useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rows = parseInt(event.target.value, 10);
    setRowsPerPage(rows);
    localStorage.setItem('logTableRowsPerPage', String(rows));
    setPage(0);
  }, []);

  const handleChangeFilter = React.useCallback((filter?: string) => {
    const lowercaseFilter = filter?.toLowerCase();
    setFilter(lowercaseFilter);

    if (lowercaseFilter) {
      localStorage.setItem('logTableFilter', lowercaseFilter);
    } else {
      localStorage.removeItem('logTableFilter');
    }
  }, []);

  const handleChange = (event: SelectChangeEvent<typeof typesFilter>) => {
    const {
      target: { value },
    } = event;
    setTypesFilter(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleToggleUTC = React.useCallback(() => {
    const newValue = !showUTC;
    setShowUTC(newValue);
    localStorage.setItem('logTableShowUTC', String(newValue));
  }, [showUTC]);

  const [formatMenuAnchor, setFormatMenuAnchor] = React.useState<null | HTMLElement>(null);

  const copyToClipboard = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>, format: 'tsv' | 'csv' | 'json' = 'tsv') => {
    if (visibleRows.length === 0) {
      alert('No logs to copy.');
      return;
    }

    if (event?.ctrlKey) {
      setFormatMenuAnchor(event.currentTarget);
      return;
    }

    let content = '';

    if (format === 'tsv') {
      const headers = logTableFields.map(field => {
        const header = logFieldHeaders[field as keyof typeof logFieldHeaders] || field;
        if (field === 'timestamp') return ['Timestamp (Local)', 'Timestamp (UTC)'];
        if (field === 'received') return ['Received (Local)', 'Received (UTC)'];
        return header;
      }).flat();
      const headerRow = headers.join('\t');

      const dataRows = visibleRows.map(log => {
        return logTableFields.map(field => {
          const value = log[field as keyof Log];
          if (value === null || value === undefined) return '';
          if (field === 'timestamp' || field === 'received') {
            return [formatDateTime(String(value)), formatDateTimeUTC(String(value))];
          }
          return String(value);
        }).flat().join('\t');
      });

      content = [headerRow, ...dataRows].join('\n');
    } else if (format === 'csv') {
      const headers = logTableFields.map(field => {
        const header = logFieldHeaders[field as keyof typeof logFieldHeaders] || field;
        if (field === 'timestamp') return ['Timestamp (Local)', 'Timestamp (UTC)'];
        if (field === 'received') return ['Received (Local)', 'Received (UTC)'];
        return header;
      }).flat();
      const headerRow = headers.map(h => `"${h}"`).join(',');

      const dataRows = visibleRows.map(log => {
        return logTableFields.map(field => {
          const value = log[field as keyof Log];
          if (value === null || value === undefined) return '""';
          if (field === 'timestamp' || field === 'received') {
            const local = formatDateTime(String(value)).replace(/"/g, '""');
            const utc = formatDateTimeUTC(String(value)).replace(/"/g, '""');
            return [`"${local}"`, `"${utc}"`];
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        }).flat().join(',');
      });

      content = [headerRow, ...dataRows].join('\n');
    } else if (format === 'json') {
      const data = visibleRows.map(log => {
        const obj: Record<string, any> = {};
        logTableFields.forEach(field => {
          const headerName = logFieldHeaders[field as keyof typeof logFieldHeaders] || String(field);
          const value = log[field as keyof Log];
          if (field === 'timestamp') {
            obj['Timestamp (Local)'] = formatDateTime(String(value));
            obj['Timestamp (UTC)'] = formatDateTimeUTC(String(value));
          } else if (field === 'received') {
            obj['Received (Local)'] = formatDateTime(String(value));
            obj['Received (UTC)'] = formatDateTimeUTC(String(value));
          } else {
            obj[headerName] = value;
          }
        });
        return obj;
      });
      content = JSON.stringify(data, null, 2);
    }

    navigator.clipboard.writeText(content)
      .then(() => {
        setSnackbarMessage(`${visibleRows.length} logs copied to clipboard as ${format.toUpperCase()}.`);
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
        setSnackbarMessage('Failed to copy to clipboard. Please try again.');
        setSnackbarOpen(true);
      });
  }, [visibleRows]);

  const handleFormatSelect = React.useCallback((format: 'tsv' | 'csv' | 'json') => {
    setFormatMenuAnchor(null);
    copyToClipboard(undefined, format);
  }, [copyToClipboard]);

  const sendDeleteCommand = React.useCallback(() => {

    const body = {} as Record<string, Set<number>>;
    let totalLogsCount = 0;

    // Delete the entire filtered set (across all pages), not just the visible
    // page — so what the active filter shows is exactly what gets deleted.
    filteredRows.forEach((row) => {
      if (row.log_id === undefined) return;

      if (!body[row.serial_number]) {
        body[row.serial_number] = new Set<number>();
      }

      body[row.serial_number].add(row.log_id);
      totalLogsCount++;
    });

    const bodyArray: Record<string, number[]> = {};
    Object.keys(body).forEach(serial => {
      bodyArray[serial] = Array.from(body[serial]);
    });

    if (totalLogsCount === 0) {
      alert('No logs to delete.');
      return;
    }

    const confirmation = window.confirm(
      hasActiveFilter
        ? `Delete ${totalLogsCount} logs matching the current filter? This action cannot be undone.`
        : `Delete ALL ${totalLogsCount} logs? This action cannot be undone.`
    );
    if (!confirmation) return;

    UserFetch('/api/logs', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bodyArray)
    })
      .then((response) => {
        if (!response.ok) {
          // Error bodies aren't always JSON (e.g. a 413 from express.json's size limit is HTML)
          return response.text().then((text) => {
            let message = `Failed to delete logs (HTTP ${response.status})`;
            try { message = JSON.parse(text).message || message; } catch { /* keep status message */ }
            throw new Error(message);
          });
        }
        return response.json();
      })
      .then((response: {
        message: string,
        results: {
          serial: string,
          deleted_count: number
        }[]
      }) => {
        const totalCount = response.results.reduce((sum, result) => sum + result.deleted_count, 0);
        alert(`${totalCount} Logs deleted successfully.`);
        logsDispatch({ type: 'RELOAD_LOGS' });
      })
      .catch((err: Error) => {
        console.error('Delete logs failed:', err);
        alert(err.message);
      });

  }, [filteredRows, hasActiveFilter, logsDispatch]);

  return <>
    <Table
    rowsNumber={filteredRows.length}
    rowsPerPageOptions={[50, 100, 500, 1000]}
    page={page}
    rowsPerPage={rowsPerPage}
    handleChangePage={handleChangePage}
    handleChangeRowsPerPage={handleChangeRowsPerPage}
    paginationLabel={filteredRows.length === logsLength
      ? `Total: ${logsLength}`
      : `Showing ${filteredRows.length} of ${logsLength}`}
    toolbar={
      <TableToolbar
        title={'Logs'}
        numSelected={0}
        autoRefreshActive={false}
        onRequestFilter={handleChangeFilter}
        filterValue={filter}
        actionBar={
          <Stack direction="row" spacing={1} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: 1 }} >
            <FormControl sx={{ m: 1, width: 150 }} size="small">
              <InputLabel id="demo-multiple-checkbox-label">Types</InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                multiple
                value={typesFilter}
                onChange={handleChange}
                input={<OutlinedInput label="Types" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {logTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={typesFilter.includes(type)} />
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={handleToggleUTC}
              variant="outlined"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                width: 'auto',
                minWidth: 'fit-content',
                maxWidth: '100%',
                borderColor: 'rgba(255, 193, 7, 0.5)',
                color: 'rgba(255, 193, 7, 0.9)',
                '&:hover': {
                  borderColor: 'rgba(255, 193, 7, 0.8)',
                  backgroundColor: 'rgba(255, 193, 7, 0.08)',
                },
              }}
            >
              {showUTC ? 'UTC' : 'Local'}
            </Button>
            <Button
              onClick={copyToClipboard}
              variant="outlined"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                width: 'auto',
                minWidth: 'fit-content',
                maxWidth: '100%',
                borderColor: 'rgba(76, 175, 80, 0.5)',
                color: 'rgba(76, 175, 80, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(76, 175, 80, 0.8)',
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                },
              }}
            >
              Copy to Clipboard
            </Button>
            <Menu
              anchorEl={formatMenuAnchor}
              open={Boolean(formatMenuAnchor)}
              onClose={() => setFormatMenuAnchor(null)}
            >
              <MenuItem onClick={() => handleFormatSelect('tsv')}>Tab-delimited (TSV)</MenuItem>
              <MenuItem onClick={() => handleFormatSelect('csv')}>CSV</MenuItem>
              <MenuItem onClick={() => handleFormatSelect('json')}>JSON</MenuItem>
            </Menu>
{isSuperAdmin && <Button
              onClick={sendDeleteCommand}
              variant="outlined"
              color="error"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                width: 'auto',
                minWidth: 'fit-content',
                maxWidth: '100%',
              }}
            >
              {hasActiveFilter ? `Delete ${filteredRows.length} matching` : `Delete all (${filteredRows.length})`}
            </Button>}
          </Stack>
        }
      />
    }
    head={<TableHead
      order={order}
      orderBy={orderBy}
      onRequestSort={handleRequestSort}
      rowCount={filteredRows.length}
      tableFields={logTableFields}
      fieldHeaders={logFieldHeaders}
    />}
  >
    {visibleRows.map((log) => {
      return (
        <LogsDeviceRow
          key={`${log.serial_number}:${log.log_id ?? log.timestamp}`}
          log={log}
          serialNumber={log.serial_number}
          showUTC={showUTC}
        />
      );
    })}
  </Table>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={2000}
      onClose={() => setSnackbarOpen(false)}
      message={snackbarMessage}
    />
  </>;
}

// Search text per log object, built on first filter and reused for every later
// filter until a reload replaces the objects (WeakMap lets old ones be collected).
const searchTextCache = new WeakMap<Log, string>();

function getSearchText(log: Log): string {
  let text = searchTextCache.get(log);
  if (text !== undefined) return text;

  const logTextParts: string[] = [];
  Object.entries(log).forEach(([key, val]) => {
    if (val === null || val === undefined) return;

    if ((key === 'timestamp' || key === 'received') && typeof val === 'string') {
      logTextParts.push(formatDateTime(val).toLowerCase());
      logTextParts.push(formatDateTimeUTC(val).toLowerCase());
    } else {
      logTextParts.push(String(val).toLowerCase());
    }
  });

  text = logTextParts.join(' ');
  searchTextCache.set(log, text);
  return text;
}

function filterLogs(logs: Log[], filter?: string): Log[] {
  if (!filter) return logs;

  const { include, exclude, exact, excludeExact } = getFilters(filter);

  if (include.length === 0 && exclude.length === 0 && exact.length === 0 && excludeExact.length === 0) return logs;

  return logs.filter(log => {
    const logText = getSearchText(log);

    const matchesIncludes = include.length === 0 ||
      include.every(term => logText.includes(term));

    const matchesExcludes = exclude.length === 0 ||
      exclude.every(term => !logText.includes(term));

    const matchesExact = exact.length === 0 ||
      exact.every(term => logText.includes(term));

    const matchesExcludeExact = excludeExact.length === 0 ||
      excludeExact.every(term => !logText.includes(term));

    return matchesIncludes && matchesExcludes && matchesExact && matchesExcludeExact;
  });
}

function getComparator<Key extends keyof Log>(order: Order, orderBy: Key): (
  a: Partial<Record<Key, number | string | boolean | object | undefined>>,
  b: Partial<Record<Key, number | string | boolean | object | undefined>>,
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default LogsTable;